import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadEntityAttachment } from '@/api/common';
import {
  createFirmware,
  checkVersionExists,
  updateFirmwareVersion,
  releaseFirmware,
  deleteFirmwares,
  firmwareAttachments,
} from '../api/firmware';
import type { CreateFirmwareRequest, CheckVersionExistsParams, DeleteFirmwaresRequest, UpdateFirmwareVersionRequest } from '../types';

export function useCreateFirmware() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateFirmwareRequest) => createFirmware(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmwares'] });
    },
  });
}

export function useCheckVersionExists() {
  return useMutation({
    mutationFn: (params: CheckVersionExistsParams) => checkVersionExists(params),
  });
}

export function useUpdateFirmwareVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateFirmwareVersionRequest) => updateFirmwareVersion(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmwares'] });
    },
  });
}

export function useUploadFirmware() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      modelId,
      firmwareId: firmwareUuid,
      file,
    }: {
      modelId: number;
      firmwareId: string;
      file: File;
    }) => {
      await uploadEntityAttachment(firmwareAttachments(modelId, firmwareUuid), {
        file,
        name: 'firmware.bin',
        mimeType: 'application/octet-stream',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmwares'] });
    },
  });
}

export function useReleaseFirmware() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, firmwareId }: { modelId: number; firmwareId: string }) =>
      releaseFirmware(modelId, firmwareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmwares'] });
    },
  });
}

export function useDeleteFirmwares() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DeleteFirmwaresRequest) => deleteFirmwares(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmwares'] });
    },
  });
}

// Bundles the firmware data mutations behind a single hook so views can pull the create,
// update, release and delete actions together, along with a combined pending flag.
export function useFirmwareMutations() {
  const create = useCreateFirmware();
  const updateVersion = useUpdateFirmwareVersion();
  const release = useReleaseFirmware();
  const remove = useDeleteFirmwares();

  const isPending = create.isPending || updateVersion.isPending || release.isPending || remove.isPending;

  return { create, updateVersion, release, remove, isPending };
}
